"use strict";
/**
 * 三套内置主题的版式。
 *
 * 在此之前，内置四套只有令牌与原型（`archetype`），没有 `layout` —— 它们走的是
 * 老路径：页头页脚硬编码、页面内容是客户自己堆的区块列表。而四层模板这套东西
 * 只有市场里的复刻稿在用。结果是"基础三套"和"新模型"是两套东西：
 * 客户用极光，就没有页头页脚的编辑入口，也没有版式可言。
 *
 * 这里把三套补齐，判据只有一条：**换一套主题，客户看到的应该是一个不同的站，
 * 而不是同一个站换了个颜色。** 所以三套的差别落在段序与组件选择上，不是只换令牌。
 *
 * 第四套（夜曲）不在这里：它与窑变同属 `showcase` 原型，直接复用 `kilnLayout`，
 * 只换令牌。版式属于**原型**，不属于主题 —— 同一原型下的两套主题段序一致，
 * 客户的 Logo、导航、页脚与页面内容才能原地留着。
 *
 * 三条纪律：
 *
 *   1) **外壳实例 id 三套共用**（`shared-*`）。客户在极光下填好的 Logo、导航、
 *      页脚，换到窑变时还在 —— 这是 WordPress 换主题"菜单还在"的等价物。
 *      页面段的 id 也尽量按语义通用（`home-hero`、`home-products`），
 *      只有语义确实不同的段才各用各的。
 *
 *   2) **props 尽量不写**。组件注册表里已有默认值，写在这里等于把默认值复制一份，
 *      以后改组件默认值，主题里那份就悄悄过期了。只写"这一套要跟别的不一样"的项。
 *
 *   3) **不写行业文案**。默认值是跨行业通用的（见 `theme-blocks.ts` 的 defaults），
 *      主题层再写一遍就等于替客户选定了行业。客户自己的内容走种子化进库。
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.kilnLayout = exports.meridianLayout = exports.auroraLayout = void 0;
/**
 * 全站外壳：工具条 + 页头（Logo / 导航 / 按钮）+ 页脚。
 *
 * 三套共用同一份。差异靠令牌表达：极光浅色页头、子午线深色页头、窑变窄条页头，
 * 都是 `color.headerBg` 的事，不需要三个不同的结构。
 *
 * 导航写成 `location: 'primary'` 而不是具体菜单项 —— 菜单项属于站点库，
 * 由站点已发布的页面生成（`apps/site/lib/nav.ts`）。
 */
const SHELL = {
    utility: [{ id: 'shared-utility', component: 'utility-bar' }],
    header: [
        { id: 'shared-logo', component: 'site-logo' },
        { id: 'shared-nav', component: 'site-nav' },
        { id: 'shared-cta', component: 'header-cta' },
    ],
    footer: [{ id: 'shared-footer', component: 'site-footer' }],
};
/** 商品列表页：标题 + 分类 + 网格。三套都要有，只是分类那块各有取舍 */
function productsPage(withCategories) {
    const main = [
        {
            id: 'products-heading',
            component: 'section-heading',
            props: { title: 'Products', align: 'start', lead: '' }, propsZh: { title: '产品' },
        },
    ];
    if (withCategories)
        main.push({ id: 'products-cats', component: 'category-grid' });
    main.push({ id: 'products-grid', component: 'product-grid', props: { limit: 12, columns: 3 } });
    return { main };
}
/** 商品详情页：主体是动态内容，主题只能决定它周围排什么 */
const PRODUCT_PAGE = {
    main: [{ id: 'product-body', component: 'product-main' }],
};
/** 普通内容页：一个富文本，客户在控制台里写 */
const CONTENT_PAGE = {
    main: [{ id: 'page-body', component: 'rich-text' }],
};
/**
 * 极光（aurora）—— 骨架 `split`，冷色、大留白、参数感。
 *
 * 首页的顺序是"先讲能力、再讲商品、最后讲流程"：适合仪器、电子、
 * 精密制造这类客户 —— 买家先确认你能不能做，再看你做什么。
 */
exports.auroraLayout = {
    version: 1,
    shared: SHELL,
    pages: {
        home: {
            hero: [
                {
                    id: 'home-hero',
                    component: 'hero-split',
                    props: { tone: 'dark', imageSide: 'start' },
                },
            ],
            main: [
                {
                    id: 'home-intro-heading',
                    component: 'section-heading',
                    props: { title: 'What we do', align: 'center' }, propsZh: { title: '我们做什么' },
                },
                { id: 'home-intro', component: 'capability-grid' },
                { id: 'home-feature', component: 'feature-split', props: { imageSide: 'end' } },
                {
                    id: 'home-products-heading',
                    component: 'section-heading',
                    props: { title: 'Products', align: 'start', lead: '' }, propsZh: { title: '产品' },
                },
                { id: 'home-products', component: 'product-grid', props: { limit: 6, columns: 3 } },
                { id: 'home-process', component: 'steps' },
            ],
        },
        products: productsPage(true),
        product: PRODUCT_PAGE,
        contact: {
            main: [
                {
                    id: 'contact-heading',
                    component: 'section-heading',
                    props: { title: 'Contact us', align: 'center', lead: '' }, propsZh: { title: '联系我们' },
                },
                { id: 'contact-form', component: 'contact' },
            ],
        },
        page: CONTENT_PAGE,
    },
};
/**
 * 子午线（meridian）—— 骨架 `editorial`，衬线标题、细线分隔、样本册式。
 *
 * 和极光的差别不是"换个主色"，而是三处结构：
 *   - 首屏用浅底、图在右（编辑感），不用深色大图；
 *   - 不放能力卡网格，改成"图文分栏 + 一段正文"，让文字承担说服；
 *   - 商品排在案例之后 —— 样本册的读法是先看做过什么，再翻产品页。
 */
exports.meridianLayout = {
    version: 1,
    shared: SHELL,
    pages: {
        home: {
            hero: [
                {
                    id: 'home-hero',
                    component: 'hero-split',
                    props: { tone: 'light', imageSide: 'end' },
                },
            ],
            main: [
                {
                    id: 'home-intro-heading',
                    component: 'section-heading',
                    props: { title: 'What we do', align: 'start', lead: '' }, propsZh: { title: '我们做什么' },
                },
                { id: 'home-feature', component: 'feature-split', props: { imageSide: 'start' } },
                { id: 'home-about', component: 'rich-text' },
                { id: 'home-proof', component: 'case-grid' },
                {
                    id: 'home-products-heading',
                    component: 'section-heading',
                    props: { title: 'Products', align: 'start', lead: '' }, propsZh: { title: '产品' },
                },
                { id: 'home-products', component: 'product-grid', props: { limit: 6, columns: 3 } },
            ],
        },
        /* 编辑风格不摆分类卡：一列商品，靠标题与留白分隔 */
        products: productsPage(false),
        product: PRODUCT_PAGE,
        contact: {
            main: [
                {
                    id: 'contact-heading',
                    component: 'section-heading',
                    props: { title: 'Contact us', align: 'start', lead: '' }, propsZh: { title: '联系我们' },
                },
                { id: 'contact-form', component: 'contact' },
                { id: 'contact-faq', component: 'faq' },
            ],
        },
        page: CONTENT_PAGE,
    },
};
/**
 * 窑变（kiln）—— 骨架 `showcase`，展示型，深底大图与横向滚动。
 *
 * 首页不先讲自己，先摆"做过什么"与"谁在用"：案例网格打头，伙伴卡片横滑，
 * 客户 Logo 墙收尾。适合建材、家居、陶瓷这类靠观感与信任成交的行业。
 */
exports.kilnLayout = {
    version: 1,
    shared: SHELL,
    pages: {
        home: {
            hero: [
                {
                    id: 'home-hero',
                    component: 'hero-split',
                    props: { tone: 'dark', imageSide: 'start' },
                },
            ],
            main: [
                { id: 'home-proof', component: 'case-grid' },
                { id: 'home-partners', component: 'partner-cards' },
                { id: 'home-intro', component: 'capability-grid', props: { columns: 4 } },
                { id: 'home-clients', component: 'logo-wall' },
                { id: 'home-cta', component: 'cta' },
                {
                    id: 'home-products-heading',
                    component: 'section-heading',
                    props: { title: 'Products', align: 'start', lead: '' }, propsZh: { title: '产品' },
                },
                { id: 'home-products', component: 'product-grid', props: { limit: 8, columns: 4 } },
            ],
        },
        products: productsPage(true),
        product: PRODUCT_PAGE,
        contact: {
            main: [
                {
                    id: 'contact-heading',
                    component: 'section-heading',
                    props: { title: 'Contact us', align: 'center', lead: '' }, propsZh: { title: '联系我们' },
                },
                { id: 'contact-form', component: 'contact' },
            ],
        },
        page: CONTENT_PAGE,
    },
};
//# sourceMappingURL=layouts.js.map